"use client";

import {
  Box,
  Button,
  Container,
  Divider,
  Group,
  List,
  rem,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useEffect } from "react";
import { ProtectedEmailLink } from "@/app/lib/components/ProtectedEmail";
import { CONTACT_EMAIL_BASE64 } from "@/app/lib/seo/siteConfig";

export default function DpaPage() {
  // Print this page as a standalone document: tag the body so the print rules
  // in global.css drop the marketing app shell's header and footer for export.
  useEffect(() => {
    document.body.classList.add("print-document");
    return () => document.body.classList.remove("print-document");
  }, []);

  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  const renderSignatureLines = (labels: string[]) => (
    <Stack gap="lg">
      {labels.map((label) => (
        <Box key={label} className="print-keep-together">
          <Box style={{ borderBottom: "1px solid var(--mantine-color-gray-5)", height: rem(28) }} />
          <Text size="xs" c="gray.6" mt={4}>
            {label}
          </Text>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Container size="md" py="xl">
      <Stack gap="lg" px={{ base: "md", md: 0 }}>
        <Box mb="xl">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Box style={{ flex: 1 }}>
              <Title order={1} mb="xs">
                Data Processing Agreement
              </Title>
              <Text size="lg" c="gray.7">
                Last Updated: June 26, 2026
              </Text>
            </Box>
            <Button
              variant="default"
              onClick={handlePrint}
              className="print-hidden"
              style={{ flexShrink: 0 }}
            >
              Save as PDF
            </Button>
          </Group>
        </Box>

        <Text>
          This Data Processing Agreement, including its Annexes (the "DPA"), forms part of the
          agreement between Orgtools and the customer ("Customer") that governs Customer's use of
          the Orgtools platform and services (the "Services") (the "Agreement"). This DPA reflects
          the parties' agreement on the processing of Personal Data in connection with the Services.
          If you require a countersigned copy for your records, contact us at{" "}
          <ProtectedEmailLink address={CONTACT_EMAIL_BASE64.privacy} />.
        </Text>

        <Text>
          In the event of a conflict between this DPA and the Agreement, this DPA controls with
          respect to the processing of Personal Data.
        </Text>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            1. Definitions
          </Title>
          <Text mb="xs">
            Capitalized terms not defined here have the meaning given in the Agreement. For this
            DPA:
          </Text>
          <List withPadding spacing="xs">
            <List.Item>
              "Data Protection Laws" means all laws and regulations applicable to the processing of
              Personal Data under the Agreement, including the EU General Data Protection Regulation
              2016/679 ("GDPR"), the UK GDPR and Data Protection Act 2018, the Swiss Federal Act on
              Data Protection, and US state privacy laws including the California Consumer Privacy
              Act as amended ("CCPA").
            </List.Item>
            <List.Item>
              "Controller", "Processor", "Data Subject", "Personal Data", "Processing", and
              "Personal Data Breach" have the meanings given in the GDPR.
            </List.Item>
            <List.Item>
              "Customer Personal Data" means Personal Data that Orgtools processes on Customer's
              behalf in providing the Services, as described in Annex I.
            </List.Item>
            <List.Item>
              "Sub-processor" means any third party engaged by Orgtools to process Customer Personal
              Data.
            </List.Item>
            <List.Item>
              "Standard Contractual Clauses" or "SCCs" means the clauses approved by the European
              Commission in Decision 2021/914 for the transfer of Personal Data to third countries.
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            2. Roles of the Parties
          </Title>
          <Text>
            With respect to Customer Personal Data, the parties acknowledge that Customer is the
            Controller, Orgtools is the Processor, and Orgtools engages Sub-processors as permitted
            by this DPA. Where Customer acts as a Processor on behalf of a third-party Controller,
            Orgtools acts as a Sub-processor and the terms of this DPA apply to that relationship.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            3. Scope and Processing Instructions
          </Title>
          <Text>
            Orgtools processes Customer Personal Data only on Customer's documented instructions,
            including with regard to international transfers, unless required to do otherwise by
            applicable law. The Agreement, this DPA, and Customer's use and configuration of the
            Services constitute Customer's complete and documented instructions. Orgtools will
            inform Customer if, in its opinion, an instruction infringes Data Protection Laws,
            unless prohibited from doing so by law.
          </Text>
          <Text mt="md">
            The subject matter, duration, nature and purpose of the processing, the types of
            Personal Data, and the categories of Data Subjects are described in Annex I.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            4. Confidentiality
          </Title>
          <Text>
            Orgtools ensures that personnel authorized to process Customer Personal Data are bound
            by appropriate confidentiality obligations and process Customer Personal Data only as
            necessary to provide the Services and as instructed by Customer.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            5. Security Measures
          </Title>
          <Text>
            Orgtools implements and maintains appropriate technical and organizational measures to
            protect Customer Personal Data against unauthorized or unlawful processing and against
            accidental loss, destruction, damage, alteration, or disclosure, taking into account the
            state of the art, the costs of implementation, and the nature, scope, context, and
            purposes of processing. These measures are described in Annex II. Orgtools may update
            its security measures over time provided that the updates do not materially reduce the
            overall level of protection.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            6. Artificial Intelligence Processing
          </Title>
          <Text>
            Certain Services use large language models to generate insights, summaries, and decision
            support. Orgtools runs these models within its cloud provider's managed environment
            (Amazon Bedrock). Customer Personal Data submitted to these features is processed inside
            that environment and is not sent to separate third-party model-provider APIs. The model
            providers whose models Orgtools uses within Amazon Bedrock do not receive, retain, or
            train their models on Customer Personal Data.
          </Text>
          <Text mt="md">
            Orgtools does not use Customer Personal Data, prompts, or outputs to train foundation
            models. Orgtools does not enable provider data-sharing or human review options for the
            models it uses to provide the Services.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            7. Sub-processors
          </Title>
          <Text>
            Customer provides general authorization for Orgtools to engage Sub-processors to process
            Customer Personal Data. A current list of Sub-processors, including their purpose and
            location, is available at orgtools.com/subprocessors.
          </Text>
          <Text mt="md">
            Orgtools imposes data protection obligations on each Sub-processor that are no less
            protective than those in this DPA, and remains responsible for each Sub-processor's
            compliance. Orgtools will notify Customer of intended changes to its Sub-processors,
            giving Customer the opportunity to object on reasonable data-protection grounds. If
            Customer reasonably objects and the parties cannot resolve the objection, Customer may
            terminate the affected Services.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            8. Assistance to Customer
          </Title>
          <Text mb="xs">
            Taking into account the nature of the processing, Orgtools assists Customer by
            appropriate technical and organizational measures, insofar as possible, in fulfilling
            Customer's obligations to:
          </Text>
          <List withPadding spacing="xs">
            <List.Item>Respond to requests from Data Subjects to exercise their rights</List.Item>
            <List.Item>
              Ensure the security of processing and notify Personal Data Breaches
            </List.Item>
            <List.Item>
              Carry out data protection impact assessments and consult supervisory authorities where
              required
            </List.Item>
          </List>
          <Text mt="md">
            If Orgtools receives a request from a Data Subject in relation to Customer Personal
            Data, Orgtools will direct the Data Subject to Customer and will not respond directly
            except as instructed by Customer or required by law.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            9. Personal Data Breach
          </Title>
          <Text>
            Orgtools notifies Customer without undue delay, and where feasible within 72 hours,
            after becoming aware of a Personal Data Breach affecting Customer Personal Data. The
            notification will describe the nature of the breach, the likely consequences, and the
            measures taken or proposed to address it, to the extent known. Orgtools will take
            reasonable steps to mitigate and remediate the breach.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            10. International Data Transfers
          </Title>
          <Text>
            Customer authorizes Orgtools to transfer Customer Personal Data outside the country of
            origin as necessary to provide the Services. Where Orgtools processes Personal Data
            subject to the GDPR and transfers it to a country that has not received an adequacy
            decision, the Standard Contractual Clauses are incorporated into this DPA by reference,
            with Customer as data exporter and Orgtools as data importer. For transfers subject to
            UK or Swiss law, the SCCs apply as supplemented by the UK International Data Transfer
            Addendum and the Swiss amendments, respectively. Module Two (Controller to Processor)
            applies where Customer is a Controller, and Module Three (Processor to Processor)
            applies where Customer is a Processor.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            11. Audits
          </Title>
          <Text>
            Orgtools makes available to Customer information reasonably necessary to demonstrate
            compliance with this DPA and allows for and contributes to audits, including
            inspections, conducted by Customer or an auditor mandated by Customer. To satisfy these
            obligations, Orgtools may provide third-party audit reports, certifications, or written
            responses to a reasonable security questionnaire. Audits are conducted no more than once
            per year, on reasonable prior notice, during business hours, and subject to
            confidentiality obligations, except where required more frequently by a supervisory
            authority.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            12. Return and Deletion
          </Title>
          <Text>
            Upon termination or expiration of the Services, Orgtools deletes or returns Customer
            Personal Data in accordance with the data retention terms described in the Orgtools
            Privacy Policy and the Agreement, and deletes existing copies unless retention is
            required by applicable law. Customer may request deletion of Customer Personal Data at
            any time as provided in the Services.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            13. US State Privacy Laws
          </Title>
          <Text>
            To the extent Orgtools processes Personal Data subject to the CCPA or other US state
            privacy laws, Orgtools acts as a "service provider" or "processor" as defined under
            those laws. Orgtools does not sell or share Customer Personal Data, does not retain,
            use, or disclose it for any purpose other than providing the Services, and does not
            combine it with Personal Data from other sources except as permitted by those laws.
            Orgtools certifies that it understands and will comply with these restrictions.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            14. Term, Liability, and Governing Law
          </Title>
          <Text>
            This DPA takes effect when the Agreement takes effect and continues until Orgtools has
            ceased all processing of Customer Personal Data. Each party's liability under this DPA
            is subject to the limitations and exclusions of liability set out in the Agreement. This
            DPA is governed by the governing law of the Agreement, or, if the Agreement does not
            specify one, the laws of the State of Washington, USA.
          </Text>
        </Box>

        {/* Execution block. Hidden on screen so the page reads as published
            standard terms, and shown only when the DPA is printed or exported
            to PDF for signature. */}
        <Box className="print-only print-keep-together">
          <Divider my="md" />
          <Title order={2} mb="md">
            Execution
          </Title>
          <Text mb="lg">
            IN WITNESS WHEREOF, the parties have caused this Data Processing Agreement to be
            executed by their duly authorized representatives. This DPA is effective as of the date
            of the last signature below.
          </Text>
          <SimpleGrid cols={2} spacing="xl">
            <Box className="print-keep-together">
              <Text fw={600} mb="lg">
                Orgtools
              </Text>
              {renderSignatureLines(["Signature", "Name", "Title", "Date"])}
            </Box>
            <Box className="print-keep-together">
              <Text fw={600} mb="lg">
                Customer
              </Text>
              {renderSignatureLines(["Company", "Signature", "Name", "Title", "Date"])}
            </Box>
          </SimpleGrid>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Annex I: Details of Processing
          </Title>
          <Table withRowBorders verticalSpacing="sm">
            <Table.Tbody>
              <Table.Tr className="print-keep-together">
                <Table.Th w={{ base: 160, md: 240 }}>Categories of Data Subjects</Table.Th>
                <Table.Td>
                  Customer's authorized users, employees, and personnel whose information Customer
                  includes in the Services.
                </Table.Td>
              </Table.Tr>
              <Table.Tr className="print-keep-together">
                <Table.Th>Types of Personal Data</Table.Th>
                <Table.Td>
                  Identification and contact data (name, email, job title), organizational and role
                  data, content Customer uploads or generates in the Services (including documents
                  and decision records), and usage data.
                </Table.Td>
              </Table.Tr>
              <Table.Tr className="print-keep-together">
                <Table.Th>Special Categories</Table.Th>
                <Table.Td>
                  None requested. The Services are not intended for special categories of Personal
                  Data, and Customer should not submit them.
                </Table.Td>
              </Table.Tr>
              <Table.Tr className="print-keep-together">
                <Table.Th>Nature and Purpose</Table.Th>
                <Table.Td>
                  Processing to provide, maintain, secure, and improve the Services, including
                  AI-assisted analysis, insights, and decision support requested by Customer.
                </Table.Td>
              </Table.Tr>
              <Table.Tr className="print-keep-together">
                <Table.Th>Duration</Table.Th>
                <Table.Td>
                  For the term of the Agreement and the retention periods described in the Privacy
                  Policy, after which data is deleted or returned.
                </Table.Td>
              </Table.Tr>
              <Table.Tr className="print-keep-together">
                <Table.Th>Frequency</Table.Th>
                <Table.Td>Continuous, for the duration of Customer's use of the Services.</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Annex II: Technical and Organizational Measures
          </Title>
          <Text mb="xs">
            Orgtools maintains the following measures, which it may update so long as the level of
            protection is not materially reduced:
          </Text>
          <List withPadding spacing="xs">
            <List.Item>
              Tenant isolation enforced at the database level through row-level security
            </List.Item>
            <List.Item>Encryption of Personal Data in transit (TLS) and at rest</List.Item>
            <List.Item>
              Role-based access controls and least-privilege access for personnel
            </List.Item>
            <List.Item>
              Logical separation of customer data and scoped processing of AI requests within the
              managed cloud environment
            </List.Item>
            <List.Item>Network controls, monitoring, and application error tracking</List.Item>
            <List.Item>Secure software development practices and dependency management</List.Item>
            <List.Item>Regular backups and documented incident response procedures</List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Contact
          </Title>
          <Text>For questions about this DPA or to request a countersigned copy, contact:</Text>
          <Text mt="xs" fw={500}>
            Orgtools
            <br />
            10768 Rocky Peak Place
            <br />
            Gig Harbor, WA 98332
            <br />
            <ProtectedEmailLink address={CONTACT_EMAIL_BASE64.privacy} />
          </Text>
        </Box>
      </Stack>
    </Container>
  );
}
